import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { combineReducers } from 'redux';
import { apiPost } from 'services/api.service';
import {
	EV_ENDPOINT_FETCH_TRIP_INSIGHT,
	EV_ENDPOINT_FETCH_TRIP_INSIGHT_GRAPH_SUMMARY2,
} from 'services/APIConstants';

// User FLAC Slice
const userFLACSlice = createSlice({
	name: 'userFLAC',
	initialState: {},
	reducers: {
		SET_USER_FLAC: (state, action) => action.payload,
	},
});

// Dashboard Loaded Slice
const dashboardLoadedSlice = createSlice({
	name: 'dashboardLoaded',
	initialState: false,
	reducers: {
		SET_DASHBOARD_LOADED: (state, action) => {
			// console.log('setting dashboard');
			return action.payload;
		},
	},
});

// Vehicle List Slice
const vehicleListSlice = createSlice({
	name: 'vehicleList',
	initialState: [],
	reducers: {
		UPDATE_VEHICLE_LIST: (state, action) => action.payload,
	},
});

// Fixed Vehicle List Slice
const fixedVehicleListSlice = createSlice({
	name: 'fixedVehicleList',
	initialState: [],
	reducers: {
		SET_ALL_VEHICLES: (state, action) => action.payload,
	},
});

// My Group Slice
const myGroupSlice = createSlice({
	name: 'myGroup',
	initialState: [],
	reducers: {
		UPDATE_MY_GROUP: (state, action) => action.payload,
	},
});

// Charge Tracker Group Tree Slice
const chargeTrackerGroupTreeSlice = createSlice({
	name: 'chargeTrackerGroupTree',
	initialState: [],
	reducers: {
		CHARGE_TRACKER_SELECTED_GROUP_TREE: (state, action) => action.payload,
	},
});

// Trip Report Group Tree Slice
const tripReportGroupTreeSlice = createSlice({
	name: 'tripReportGroupTree',
	initialState: [],
	reducers: {
		TRIP_REPORT_SELECTED_GROUP_TREE: (state, action) => action.payload,
	},
});

// Trip Insight Group Tree Slice
const tripInsightGroupTreeSlice = createSlice({
	name: 'tripInsightGroupTree',
	initialState: [],
	reducers: {
		TRIP_INSIGHT_SELECTED_GROUP_TREE: (state, action) => action.payload,
	},
});

// Showing Data For Slice
const showingDataForSlice = createSlice({
	name: 'showingDataFor',
	initialState: {},
	reducers: {
		SHOWING_DATA_FOR: (state, action) => action.payload,
	},
});

// Charge Tracker Slice
const chargeTrackerSlice = createSlice({
	name: 'chargetracker',
	initialState: null,
	reducers: {
		UPDATE_CHARGE_TRACKER_DATA: (state, action) => action.payload,
	},
});

// Charge Tracker Live Slice
// In your global reducer
const chargeTrackerLiveSlice = createSlice({
	name: 'chargetrackerLive',
	initialState: null,
	reducers: {
		UPDATE_CHARGE_TRACKER_DATA_LIVE: (state, action) => {
			// console.log('Previous state:', state);
			// console.log('Action payload:', action.payload);
			return JSON.parse(JSON.stringify(action.payload));
		},
	},
});

// Charge Dash Slice
const chargeDashSlice = createSlice({
	name: 'chargeDash',
	initialState: null,
	reducers: {
		UPDATE_CHARGE_TRACKER_DASH: (state, action) => action.payload,
	},
});

// Trip Report Slice
const tripReportSlice = createSlice({
	name: 'tripReport',
	initialState: null,
	reducers: {
		UPDATE_TRIP_REPORT_DATA: (state, action) => action.payload,
	},
});

// Create async thunks for Trip Insight
export const fetchTripInsightData = createAsyncThunk(
	'tripInsight/fetchData',
	async ({ starttime, endtime, groupid }) => {
		const response = await apiPost(EV_ENDPOINT_FETCH_TRIP_INSIGHT, {
			starttime,
			endtime,
			...(groupid && { groupid }),
		});
		if (response.status === 'CRASHED') {
			throw new Error('Failed to fetch trip insight data');
		}
		return response.data;
	}
);

export const fetchTripInsightGraphSummary = createAsyncThunk(
	'tripInsight/fetchGraphSummary',
	async ({ starttime, endtime, groupid, limittype }) => {
		const response = await apiPost(EV_ENDPOINT_FETCH_TRIP_INSIGHT_GRAPH_SUMMARY2, {
			starttime,
			endtime,
			groupid,
			limittype,
		});
		return response.data;
	}
);

// Enhanced Trip Insight Slice
const tripInsightSlice = createSlice({
	name: 'tripInsight',
	initialState: {
		data: null,
		graphData: {
			distance: [],
			socCharged: [],
			chargeDuration: [],
			roc: [],
			socConsumed: [],
			range: [],
		},
		status: 'idle',
		error: null,
	},
	reducers: {
		UPDATE_TRIP_INSIGHT_DATA: (state, action) => {
			state.data = action.payload;
			state.status = 'succeeded';
			state.error = null;
		},
		CLEAR_TRIP_INSIGHT_GRAPH_DATA: (state) => {
			state.graphData = {
				distance: [],
				socCharged: [],
				chargeDuration: [],
				roc: [],
				socConsumed: [],
				range: [],
			};
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchTripInsightData.pending, (state) => {
				state.status = 'loading';
			})
			.addCase(fetchTripInsightData.fulfilled, (state, action) => {
				state.status = 'succeeded';
				state.data = action.payload;
				state.error = null;
			})
			.addCase(fetchTripInsightData.rejected, (state, action) => {
				state.status = 'failed';
				state.error = action.error.message;
			})
			.addCase(fetchTripInsightGraphSummary.fulfilled, (state, action) => {
				const [data] = action.payload;
				const { limittype } = action.meta.arg;

				limittype.forEach((type) => {
					switch (type) {
						case 'distance':
							state.graphData.distance = data.distance || [];
							break;
						case 'soccharged':
							state.graphData.socCharged = data.soccharged || [];
							break;
						case 'chargeduration':
							state.graphData.chargeDuration = data.chargeduration || [];
							break;
						case 'roc':
							state.graphData.roc = data.roc || [];
							break;
						case 'socconsumed':
							state.graphData.socConsumed = data.socconsumed || [];
							break;
						case 'range':
							state.graphData.range = data.range || [];
							break;
						default:
							break;
					}
				});
			});
	},
});

// Toast Slice
const toastSlice = createSlice({
	name: 'toast',
	initialState: null,
	reducers: {
		// ADD_TOAST: (state, action) => action.payload,
		addToast: (state, action) => action.payload,
	},
});

// Export all actions
export const { SET_USER_FLAC: setUserFLAC } = userFLACSlice.actions;
export const { SET_DASHBOARD_LOADED: setDashboardLoaded } = dashboardLoadedSlice.actions;
export const { UPDATE_VEHICLE_LIST: updateVehicleList } = vehicleListSlice.actions;
export const { SET_ALL_VEHICLES: setAllVehicles } = fixedVehicleListSlice.actions;
export const { UPDATE_MY_GROUP: updateMyGroup } = myGroupSlice.actions;
export const { CHARGE_TRACKER_SELECTED_GROUP_TREE: setChargeTrackerGroupTree } =
	chargeTrackerGroupTreeSlice.actions;
export const { TRIP_REPORT_SELECTED_GROUP_TREE: setTripReportGroupTree } =
	tripReportGroupTreeSlice.actions;
export const { TRIP_INSIGHT_SELECTED_GROUP_TREE: setTripInsightGroupTree } =
	tripInsightGroupTreeSlice.actions;
export const { SHOWING_DATA_FOR: setShowingDataFor } = showingDataForSlice.actions;
export const { UPDATE_CHARGE_TRACKER_DATA: updateChargeTrackerData } = chargeTrackerSlice.actions;
export const { UPDATE_CHARGE_TRACKER_DATA_LIVE: updateChargeTrackerDataLive } =
	chargeTrackerLiveSlice.actions;
export const { UPDATE_CHARGE_TRACKER_DASH: updateChargeDash } = chargeDashSlice.actions;
export const { UPDATE_TRIP_REPORT_DATA: updateTripReportData } = tripReportSlice.actions;
export const {
	UPDATE_TRIP_INSIGHT_DATA: updateTripInsightData,
	CLEAR_TRIP_INSIGHT_GRAPH_DATA: clearTripInsightGraphData,
} = tripInsightSlice.actions;
export const { addToast } = toastSlice.actions;

// Create root reducer
const createRootReducer = () => {
	const combinedReducer = combineReducers({
		userFLAC: userFLACSlice.reducer,
		dashboardLoaded: dashboardLoadedSlice.reducer,
		vehicleList: vehicleListSlice.reducer,
		myGroup: myGroupSlice.reducer,
		chargetracker: chargeTrackerSlice.reducer,
		tripReport: tripReportSlice.reducer,
		toast: toastSlice.reducer,
		tripInsight: tripInsightSlice.reducer,
		chargetrackerLive: chargeTrackerLiveSlice.reducer,
		chargeDash: chargeDashSlice.reducer,
		fixedVehicleList: fixedVehicleListSlice.reducer,
		chargeTrackerGroupTree: chargeTrackerGroupTreeSlice.reducer,
		tripReportGroupTree: tripReportGroupTreeSlice.reducer,
		tripInsightGroupTree: tripInsightGroupTreeSlice.reducer,
		showingDataFor: showingDataForSlice.reducer,
	});

	return (state, action) => {
		// console.log('Root reducer called with action:', action);
		return combinedReducer(state, action);
	};
};

// Selectors
export const selectTripInsightData = (state) => state.tripInsight.data;
export const selectTripInsightStatus = (state) => state.tripInsight.status;
export const selectTripInsightError = (state) => state.tripInsight.error;
export const selectTripInsightGraphData = (state) => state.tripInsight.graphData;

export default createRootReducer;
